import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import API from "../api/axios";
import { useAuth } from "../context/AuthContext";
import {
  MdCheckCircle, MdError,
  MdEdit, MdSave,
} from "react-icons/md";
import {
  RadialBarChart, RadialBar,
  ResponsiveContainer, Tooltip,
  Legend,
} from "recharts";

const SLA = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "NETWORK_ADMINISTRATOR";
  const [slaData, setSlaData] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSLA();
    fetchReport();
  }, []);

  const fetchSLA = async () => {
    try {
      const res = await API.get("/sla");
      setSlaData(res.data);
    } catch {
      setSlaData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const res = await API.get("/sla/report");
      setReport(res.data);
    } catch {
      setReport(null);
    }
  };

  const handleSave = async (deviceId) => {
    setSaving(true);
    try {
      await API.post("/sla", {
        deviceId,
        targetUptime: editValue,
      });
      await fetchSLA();
      await fetchReport();
      setEditId(null);
    } catch {
      alert("Failed to update SLA");
    } finally {
      setSaving(false);
    }
  };

  const chartData = report
    ? [
        { name: "Met", value: report.met, fill: "#16a34a" },
        { name: "Breached", value: report.breached, fill: "#dc2626" },
      ]
    : [];

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">SLA Tracking</h2>
        <p className="text-sm text-gray-500">
          Service Level Agreement monitoring (Last 30 days)
        </p>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Radial Chart */}
          <div className="bg-white rounded-xl shadow p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              SLA Overview
            </h3>
            <ResponsiveContainer width="100%" height={180}>
              <RadialBarChart
                innerRadius="40%"
                outerRadius="90%"
                data={chartData}
                startAngle={180}
                endAngle={0}
              >
                <RadialBar dataKey="value" label={{ fill: "#374151", fontSize: 12 }} />
                <Tooltip />
                <Legend />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>

          {/* Met Card */}
          <div className="bg-green-50 rounded-xl shadow p-5 flex items-center gap-4">
            <MdCheckCircle size={40} className="text-green-500" />
            <div>
              <p className="text-sm text-gray-500">SLA Met</p>
              <p className="text-4xl font-bold text-green-600">{report.met}</p>
              <p className="text-xs text-gray-400">
                out of {report.total} devices
              </p>
            </div>
          </div>

          {/* Breached Card */}
          <div className="bg-red-50 rounded-xl shadow p-5 flex items-center gap-4">
            <MdError size={40} className="text-red-500" />
            <div>
              <p className="text-sm text-gray-500">SLA Breached</p>
              <p className="text-4xl font-bold text-red-600">
                {report.breached}
              </p>
              <p className="text-xs text-gray-400">
                out of {report.total} devices
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SLA Table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading SLA data...</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-6 py-3 text-left">Device</th>
                <th className="px-6 py-3 text-left">IP Address</th>
                <th className="px-6 py-3 text-left">Target Uptime</th>
                <th className="px-6 py-3 text-left">Actual Uptime</th>
                <th className="px-6 py-3 text-left">Total Checks</th>
                <th className="px-6 py-3 text-left">SLA Status</th>
                {isAdmin && (
                  <th className="px-6 py-3 text-left">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {slaData.map((item) => (
                <tr key={item.deviceId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">
                    {item.deviceName}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {item.ipAddress}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {editId === item.deviceId ? (
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-20 border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none"
                      />
                    ) : (
                      `${item.targetUptime}%`
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            item.met ? "bg-green-500" : "bg-red-500"
                          }`}
                          style={{
                            width: `${Math.min(item.actualUptime, 100)}%`,
                          }}
                        />
                      </div>
                      <span
                        className={`text-xs font-medium ${
                          item.met ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {item.actualUptime}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {item.totalChecks}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`flex items-center gap-1 text-xs font-medium ${
                        item.met ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {item.met ? (
                        <MdCheckCircle size={16} />
                      ) : (
                        <MdError size={16} />
                      )}
                      {item.met ? "Met" : "Breached"}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4">
                      {editId === item.deviceId ? (
                        <button
                          onClick={() => handleSave(item.deviceId)}
                          disabled={saving}
                          className="flex items-center gap-1 text-green-600 hover:text-green-800 text-xs"
                        >
                          <MdSave size={16} />
                          {saving ? "Saving..." : "Save"}
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setEditId(item.deviceId);
                            setEditValue(item.targetUptime);
                          }}
                          className="flex items-center gap-1 text-blue-500 hover:text-blue-700 text-xs"
                        >
                          <MdEdit size={16} />
                          Edit
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};

export default SLA;